import { DeliveryEventStatus, DeliveryEventVendorFinancialDetails, TBillingAddress } from './types';
import { formatDate } from '@/helpers';
import { DayjsFormat } from '@/helpers/date-time';
import { EventVendorLocation } from '../Dashboard.page/types';

// Aggregated delivery cards carry every event vendor id on their manifest as a
// comma-joined route param (see aggregateDeliveryEventVendors).
export const parseEventVendorIds = (id: string) => {
  const ids = id.split(',').filter(Boolean);
  return ids.length ? ids : [id];
};

type SummaryField = keyof DeliveryEventVendorFinancialDetails;
type SummaryMember = Partial<Record<SummaryField, number | null>>;

const FINANCIAL_SUMMARY_FIELDS: SummaryField[] = [
  'summaryOrderCount',
  'summaryItemCount',
  'summaryFoodSalesCents',
  'summaryTaxCents',
  'summaryTaxRemittedByFoodaCents',
  'summaryCommissionCents',
  'summaryPpFeesCents',
  'summaryPreliminaryEventTotalCents',
];

// Every field is linear across a manifest's events, so summing per-event totals
// is exact. A missing value means the backend failed to calculate that event, so
// the total is null rather than an understated figure.
export const sumSummaryField = (members: SummaryMember[], field: SummaryField) => {
  if (!members.length) return null;

  let total = 0;
  for (const member of members) {
    const value = member[field];
    if (value == null) return null;
    total += value;
  }

  return total;
};

export const sumFinancialSummaries = (members: SummaryMember[]) => {
  const totals = {} as DeliveryEventVendorFinancialDetails;

  for (const field of FINANCIAL_SUMMARY_FIELDS) {
    const total = sumSummaryField(members, field);
    if (total === null) return null;
    totals[field] = total;
  }

  return totals;
};

export const getDeliveryEventColor = (status?: DeliveryEventStatus) => {
  switch (status) {
    case DeliveryEventStatus.CONFIRMED:
      return 'green';
    case DeliveryEventStatus.UNCONFIRMED:
      return 'red';
    default:
      return 'yellow';
  }
};

export const formatEventTime = (time?: string, noTime = '-') => {
  if (!time) return noTime;
  return formatDate(time, DayjsFormat.TIME_12H_LOWER);
};

export const getBillingAddress = (
  address: TBillingAddress,
  location?: EventVendorLocation['location']
) => {
  if (!address) return '';
  const billingAddress = [
    `${address.address1 || address.address2}`,
    `${address.city} ${address.state} ${address.zipCode}`,
  ]
    .filter((string) => string.trim())
    .join(', ');

  if (location) {
    const locationAddress = [
      `${location.building.address.address1 || location.building.address.address2}`,
      `${location.building.address.city} ${location.building.address.state} ${location.building.address.zipCode}`,
    ]
      .filter((string) => string.trim())
      .join(', ');

    if (billingAddress === locationAddress) {
      return '';
    }
  }

  return billingAddress;
};

export const getRestaurantInfo = (
  restaurant: string,
  address: TBillingAddress,
  location?: EventVendorLocation['location']
) => {
  const billingAddress = getBillingAddress(address, location);
  if (billingAddress) {
    return `${restaurant}, ${billingAddress}`;
  }
  return restaurant;
};
